import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Users, ArrowLeft, DollarSign, Database, Server, Loader2, RefreshCw,
  Download, PieChart, BarChart3, TrendingUp
} from 'lucide-react';
import {
  BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { useSnowflakeCredentials } from '../components/SnowflakeConnect';
import SnowflakeConnect from '../components/SnowflakeConnect';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';
import axios from 'axios';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#0ea5e9', '#84cc16'];

const TeamChargeback = () => {
  const [loading, setLoading] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [chargebackData, setChargebackData] = useState(null);
  const [days, setDays] = useState(30);
  const { credentials, isConnected, updateCredentials } = useSnowflakeCredentials();
  const { shouldShowData } = useDemoData();

  const fetchData = async () => {
    if (!isConnected) {
      if (shouldShowData) {
        setChargebackData(getMockData());
      }
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/snowflake/advanced/chargeback`, {
        credentials,
        days
      });
      setChargebackData(response.data);
      toast.success('Chargeback data refreshed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch chargeback data');
    } finally {
      setLoading(false);
    }
  };

  const getMockData = () => ({
    totals: {
      total_credits: 8934,
      total_cost_usd: 22335,
      unique_users: 12,
      unique_roles: 5,
      unique_warehouses: 4
    },
    by_user: [
      { USER_NAME: 'data_team@company.com', QUERY_COUNT: 4521, CREDITS: 2345, cost_usd: 5862.50, percent_of_total: 26.2 },
      { USER_NAME: 'john.smith@company.com', QUERY_COUNT: 1234, CREDITS: 1876, cost_usd: 4690.00, percent_of_total: 21.0 },
      { USER_NAME: 'analytics_svc', QUERY_COUNT: 8765, CREDITS: 1654, cost_usd: 4135.00, percent_of_total: 18.5 },
      { USER_NAME: 'jane.doe@company.com', QUERY_COUNT: 567, CREDITS: 987, cost_usd: 2467.50, percent_of_total: 11.0 },
      { USER_NAME: 'etl_pipeline', QUERY_COUNT: 12345, CREDITS: 876, cost_usd: 2190.00, percent_of_total: 9.8 },
      { USER_NAME: 'reporting_user', QUERY_COUNT: 345, CREDITS: 654, cost_usd: 1635.00, percent_of_total: 7.3 },
      { USER_NAME: 'ml_training', QUERY_COUNT: 123, CREDITS: 542, cost_usd: 1355.00, percent_of_total: 6.2 },
    ],
    by_role: [
      { ROLE_NAME: 'DATA_ENGINEER', UNIQUE_USERS: 3, QUERY_COUNT: 15234, CREDITS: 3456 },
      { ROLE_NAME: 'ANALYST', UNIQUE_USERS: 5, QUERY_COUNT: 4532, CREDITS: 2345 },
      { ROLE_NAME: 'DATA_SCIENTIST', UNIQUE_USERS: 2, QUERY_COUNT: 890, CREDITS: 1567 },
      { ROLE_NAME: 'SERVICE_ACCOUNT', UNIQUE_USERS: 4, QUERY_COUNT: 21110, CREDITS: 1234 },
      { ROLE_NAME: 'DEVELOPER', UNIQUE_USERS: 3, QUERY_COUNT: 567, CREDITS: 332 },
    ],
    by_warehouse: [
      { WAREHOUSE_NAME: 'ANALYTICS_WH', CREDITS: 3456, ACTIVE_DAYS: 28, cost_usd: 8640 },
      { WAREHOUSE_NAME: 'ETL_WH', CREDITS: 2789, ACTIVE_DAYS: 30, cost_usd: 6972.50 },
      { WAREHOUSE_NAME: 'REPORTING_WH', CREDITS: 1567, ACTIVE_DAYS: 25, cost_usd: 3917.50 },
      { WAREHOUSE_NAME: 'DEV_WH', CREDITS: 1122, ACTIVE_DAYS: 20, cost_usd: 2805 },
    ]
  });

  useEffect(() => {
    fetchData();
  }, [isConnected, days]);

  const handleConnected = (creds) => {
    updateCredentials(creds);
    setShowConnect(false);
  };

  const exportCSV = () => {
    if (!chargebackData) return;
    
    let csv = 'User/Team,Query Count,Credits,Cost (USD),% of Total\n';
    chargebackData.by_user.forEach(u => {
      csv += `${u.USER_NAME},${u.QUERY_COUNT},${u.CREDITS},${u.cost_usd},${u.percent_of_total}%\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chargeback_report_${days}days.csv`;
    a.click();
    toast.success('Report exported!');
  };

  const displayData = chargebackData || (shouldShowData ? getMockData() : null);

  if (!displayData) {
    return (
      <div className="space-y-6" data-testid="team-chargeback">
        <EmptyDataState title="Team Chargeback" description="Connect your Snowflake account to see team cost allocation and chargeback reports." />
      </div>
    );
  }

  const pieData = displayData.by_user.slice(0, 6).map((u, idx) => ({
    name: u.USER_NAME.split('@')[0],
    value: u.cost_usd,
    color: COLORS[idx % COLORS.length]
  }));

  return (
    <div className="space-y-6" data-testid="team-chargeback">
      <SnowflakeConnect 
        isOpen={showConnect} 
        onClose={() => setShowConnect(false)}
        onConnected={handleConnected}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/snowflake">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
              <Users className="w-7 h-7 text-emerald-400" />
              <BrandTitle>Team Chargeback</BrandTitle>
            </h1>
            <p className="text-slate-700 text-sm">Cost allocation by user, role, and warehouse</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select 
            value={days} 
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <Button variant="outline" onClick={exportCSV}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-0 shadow-md bg-emerald-50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-800">
          <CardContent className="p-5 text-center">
            <DollarSign className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm text-slate-400">Total Cost</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">${displayData.totals.total_cost_usd.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-5 text-center">
            <Database className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm text-slate-400">Total Credits</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{displayData.totals.total_credits.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-5 text-center">
            <Users className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm text-slate-400">Users</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{displayData.totals.unique_users}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-5 text-center">
            <BarChart3 className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="text-sm text-slate-400">Roles</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{displayData.totals.unique_roles}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-5 text-center">
            <Server className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <p className="text-sm text-slate-400">Warehouses</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{displayData.totals.unique_warehouses}</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Cost by User Pie */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-emerald-500" />
              Cost Distribution by User
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                <RechartsPie>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({name, percent}) => `${name} (${(percent*100).toFixed(0)}%)`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Cost']} />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Cost by Warehouse Bar */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="w-5 h-5 text-emerald-500" />
              Cost by Warehouse
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                <BarChart data={displayData.by_warehouse}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="WAREHOUSE_NAME" stroke="#94a3b8" fontSize={10} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }}
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Cost']}
                  />
                  <Bar dataKey="cost_usd" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Tables */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-500" />
            User Cost Breakdown
          </CardTitle>
          <CardDescription>Detailed cost allocation for chargeback</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">User / Team</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Queries</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Credits</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Cost (USD)</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {displayData.by_user.map((user, idx) => (
                  <tr key={user.USER_NAME} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                          style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                        >
                          {user.USER_NAME[0].toUpperCase()}
                        </div>
                        <span className="font-medium text-slate-900 dark:text-white">{user.USER_NAME}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right text-slate-400 dark:text-slate-400">{user.QUERY_COUNT.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right text-slate-400 dark:text-slate-400">{user.CREDITS.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right font-semibold text-slate-900 dark:text-white">${user.cost_usd.toLocaleString()}</td>
                    <td className="py-3 px-4 text-right">
                      <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                        {user.percent_of_total}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamChargeback;
